import React from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
} from "@mui/material";
import {
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
  EmailOutlined as EmailIcon,
  CallOutlined as CallIcon,
} from "@mui/icons-material";
import { Card, CardAction } from "../types";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { RootState } from "../../../store/store";
import { GetDownloadPremiumCertificate } from "../../Dashboard/DashboardAction";
import { getBufferData } from "../../../utils/Utility";
import { setDownloadIdCardContent } from "../../Dashboard/DashboardSlice";
import { resetCommonSlice } from "../../../store/common/CommonSlice";

interface DocumentDetailsCardProps {
  card: Card;
  onAction?: (action: CardAction) => void;
}

const DocumentDetailsCard: React.FC<DocumentDetailsCardProps> = ({
  card,
  onAction,
}) => {
  const dispatch = useAppDispatch();
  const { isSuccess, isError, calledReducerType } = useAppSelector((s: RootState) => s.common);
  const { downloadIdCardContent } = useAppSelector((s: RootState) => s.dashboard);

  const handleDownload = () => {
    dispatch(
      GetDownloadPremiumCertificate({
        policyId: "0987",
        lobCode: "PA",
      })
    );
  };

  React.useEffect(() => {
    if (
      isSuccess &&
      calledReducerType === "GetDownloadPremiumCertificate" &&
      downloadIdCardContent
    ) {
      const bufferArray = getBufferData(downloadIdCardContent);
      const url = URL.createObjectURL(new Blob([bufferArray], { type: "application/pdf" }));
      const downloadLink = document.createElement("a");
      const docId = card?.entity_refs?.document_id;
      downloadLink.download = docId ? `Document-${docId}.pdf` : "Document.pdf";
      downloadLink.href = url;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(url);
      dispatch(setDownloadIdCardContent(null));
    }
  }, [calledReducerType, isSuccess, downloadIdCardContent, dispatch, card?.entity_refs?.document_id]);

  React.useEffect(() => {
    if (isError && calledReducerType === "GetDownloadPremiumCertificate") {
      alert(`Error in downloading document`);
      dispatch(setDownloadIdCardContent(null));
      dispatch(resetCommonSlice());
    }
  }, [calledReducerType, isError, dispatch]);

  const formatAsOf = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  const handleActionClick = (action: CardAction) => {
    const actionLabel = (action?.label || "").toString().toLowerCase();
    const actionId = (action?.id || "").toString().toLowerCase();
    if (actionLabel.includes("email") || actionId.includes("email")) return;
    if (actionLabel.includes("call") || actionId.includes("call")) return;
    if (actionLabel.includes("download") || actionId.includes("download")) {
      handleDownload();
      return;
    }
    if (!onAction) return;
    onAction(action);
  };

  const sections = card?.sections || [];
  const secondaryActions = card?.cta?.secondary || [];
  const allActions = [
    ...(card?.cta?.primary ? [card.cta.primary] : []),
    ...secondaryActions,
  ];

  const getActionIcon = (action: CardAction) => {
    const key = `${action?.label || ""} ${action?.id || ""}`.toLowerCase();
    if (key.includes("download")) return <DownloadIcon sx={{ fontSize: 18 }} />;
    if (key.includes("email")) return <EmailIcon sx={{ fontSize: 18 }} />;
    if (key.includes("call")) return <CallIcon sx={{ fontSize: 18 }} />;
    return undefined;
  };

  return (
    <Box sx={{ height: "auto", p: 2, backgroundColor: "#f3f4f6" }}>
      <Paper
        elevation={0}
        sx={{
          p: 0,
          maxWidth: 470,
          mx: "auto",
          borderRadius: "14px",
          backgroundColor: "white",
          border: "1px solid #e5e7eb",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            background: "linear-gradient(135deg, #4a235f 0%, #b4234b 100%)",
            color: "white",
            px: 3,
            py: 2.5,
            display: "flex",
            alignItems: "center",
            gap: 1.25,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              backgroundColor: "rgba(255,255,255,0.18)",
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
            }}
          >
            <FileIcon sx={{ fontSize: 20 }} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: "20px", lineHeight: 1.1, fontFamily: "serif" }}>
            {card.title || "Document Details"}
          </Typography>
        </Box>

        <Box sx={{ px: 3, py: 2.5 }}>
          {card.subtitle && (
            <Typography sx={{ fontWeight: 700, fontSize: "16px", mb: 0.5 }}>
              {card.subtitle}
            </Typography>
          )}
          {card.as_of && (
            <Typography sx={{ color: "#6b7280", display: "block", fontSize: "13px" }}>
              As of {formatAsOf(card.as_of)}
            </Typography>
          )}
        </Box>

        <Box sx={{ px: 3 }}>
          <Box sx={{ borderTop: "3px solid #d1b9c6" }} />
        </Box>

        {sections.map((section, sIdx) => (
          <Box
            key={`doc_section_${sIdx}`}
            sx={{
              px: 3,
              py: 2.5,
              borderBottom: sIdx < sections.length - 1 ? "1px solid #eadfe6" : "none",
            }}
          >
            {section?.heading && (
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: "12px",
                  mb: 1.5,
                  background: "linear-gradient(90deg, #4a235f 0%, #b4234b 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {section.heading}
              </Typography>
            )}
            <Stack spacing={1.5}>
              {(section?.items || []).map((item, iIdx) => (
                <Box key={`doc_item_${sIdx}_${iIdx}`}>
                  <Typography
                    sx={{ fontWeight: 500, color: "#b4234b", fontSize: "13px", mb: 0.25 }}
                  >
                    {item.label}
                  </Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: "16px", lineHeight: 1.2, wordBreak: "break-word" }}>
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        ))}

        {(card.disclaimers || []).length > 0 && (
          <Box sx={{ px: 3, py: 2.5, backgroundColor: "#fafafa", borderTop: "1px solid #eadfe6" }}>
            <Typography sx={{ fontSize: "12px", color: "#666" }}>
              {(card.disclaimers || []).join(" ")}
            </Typography>
          </Box>
        )}

        {allActions.length > 0 && (
          <Box sx={{ px: 3, pb: 3 }}>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              {allActions.map((action, index) => (
                <Button
                  key={`doc_action_${index}`}
                  variant="contained"
                  startIcon={getActionIcon(action)}
                  onClick={() => handleActionClick(action)}
                  sx={{
                    flex: 1,
                    minWidth: "120px",
                    background: "linear-gradient(135deg, #b4234b 0%, #4a235f 100%)",
                    color: "#fff",
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "14px",
                    py: 1.25,
                    "&:hover": {
                      background: "linear-gradient(135deg, #981f42 0%, #3f1d52 100%)",
                    },
                  }}
                >
                  {action.label}
                </Button>
              ))}
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default DocumentDetailsCard;
